// The inventory of every audit type the product writes, and which Servis tool
// it belongs to.
//
// 🔴 F138 kolečko 2: this list used to live inside `worker/src/routes/admin-diag.ts`,
// which imports Hono and therefore cannot enter the browser bundle. So the
// client's own history pane (`src/activity.tsx`) carried a SECOND, hand-typed
// list of 57 types - and it had drifted: 53 of the 109 types the classification
// knows had no Czech sentence at all, so a partner reading a client's history
// met "Systémová událost: deliverable published" - an internal code with its
// underscores swapped for spaces, in the middle of a Czech sentence.
//
// One inventory, two readers: the Servis timeline classifies by it, the client
// history writes a sentence for each of its entries, and
// `tests/aktivita-f138.test.ts` fails on any entry without a sentence in BOTH
// locales. A type added to this file tomorrow forces its own sentence without
// anybody remembering.

/**
 * Which Servis tool an audit type belongs to. Anything unlisted falls into
 * `system`, which is queried as NOT IN (everything listed) so the buckets stay
 * a partition: no event can hide between two filters.
 */
export const ACTIVITY_TOOL_TYPES: Record<string, readonly string[]> = {
  calls: [
    "call_created", "call_updated", "call_deleted", "call_cancelled", "call_enrolled",
    "call_enrollment_updated", "call_series_created", "call_series_cancelled", "call_series_extended",
    "marketing_call_completed", "unlock_code_created", "unlock_code_revoked", "unlock_code_renewed",
    "stable_call_link_redeemed", "stable_call_links_ensured",
    // F138 kolečko 3: renaming the shared /go/ slug is a call fact and the
    // only place an admin would look for it.
    "global_unlock_link_changed"
  ],
  team: [
    "staff_created", "staff_role_changed", "staff_deactivated", "staff_reactivated",
    // S3-04: written by the member profile save in Tým. Unlisted, it fell into
    // "Systém", so an administrator looking for the change they had just made
    // in Tým did not find it under Tým.
    "staff_profile_updated",
    "magic_link_issued", "sessions_revoked", "partner_message_sent",
    "partner_material_added", "partner_material_removed"
  ],
  clients: [
    "client_initialized", "client_updated", "client_confirmation_reverted", "client_step_confirmed",
    "client_selling", "manual_state_forward", "manual_state_regression", "milestone_set",
    "onboarding_completed", "beta_feedback_started", "buddy_ready", "bot_transfer_ready",
    "bot_acceptance_reported", "app_upsell_interest", "apple_account_started", "app_wizard_prefilled",
    "app_wizard_submitted", "handover_telegram_submitted",
    // F118: the team recording that the instance owner role is set. Unlisted
    // it would fall into "Systém" and read as an unknown change in the one
    // feed an admin opens to see what happened to a client.
    "handover_owner_role_set", "questionnaire_submitted",
    "questionnaire_submission_reverted", "q1_ready", "q2_ready", "q2_submitted_waiting",
    "knowhow_resources_ready", "knowhow_uploaded", "knowhow_folder_shared",
    "knowhow_opened_from_sharing_email", "resource_updated", "resource_deleted",
    "stripe_connect_url_set", "stripe_connected", "stripe_invite_confirmed", "instance_team_requested",
    "instance_team_done", "training_result_received",
    // 🔴 F138 kolečko 2, found by sweeping every `audit(...)` call site rather
    // than by reading this list: the T9 training lane writes both of these and
    // neither was classified, so a training that started or died read as an
    // unknown system event in the two feeds an operator opens to find out why
    // a client's bot is quiet.
    "training_run_started", "training_run_failed",
    // Deleting a milestone is a decision about the client, next to setting one.
    "milestone_deleted",
    // F112: the five bot-transfer facts. Unlisted they fell into "Systém" and
    // read as "Neznámá změna" - in the one feed an admin opens to see what
    // happened to a client's transfer. `bot_transfer_failed` also becomes an
    // `error` row by itself (activityOutcome reads the _failed suffix).
    "bot_transfer_job_queued", "bot_transfer_done", "bot_transfer_failed",
    "bot_transfer_returned_to_client", "bot_transfer_recipient_refused",
    // B1 (audit F115): rewriting the owner's address is a client fact, and the
    // one an admin goes looking for when a client says no mail ever arrived.
    "client_owner_email_changed",
    // 🔴 F138 kolečko 3. Thirteen types were live on the client the verdict was
    // written from and NONE of them had a sentence, so seven rows in a row read
    // "Zaznamenali jsme změnu u klienta." - a history that records nothing. The
    // list came from a sweep of every `audit(...)` call site in worker/src UNION
    // `SELECT DISTINCT type FROM audit_events` on dev, so it covers the types
    // nobody will write again as well as the ones that fire every day.
    "bot_transferred_automatically", "bot_transfer_false_done_repaired",
    "owner_role_job_queued", "owner_role_done", "owner_role_failed", "owner_role_id_refused",
    "knowhow_handover_verified", "instance_folder_recorded", "license_key_imported",
    "app_release_status_changed", "training_resume_requested",
    // A partner deciding FOR the client is a decision about the client (F104);
    // its twin `staff_override_reopened` was classified here from the start.
    "staff_override_approved",
    // F171: "Napojení plateb", the partner's checklist, its completion, its
    // way back, and the admin switch that gives a client the step.
    "payments_setup_checklist_updated", "payments_setup_completed", "payments_setup_reopened",
    "payments_setup_flag_changed"
  ],
  materials: [
    "deliverable_created", "deliverable_updated", "deliverable_published", "deliverable_client_approved",
    "deliverable_feedback_added", "feedback_processed", "sales_edit_requested", "sales_edit_claimed",
    "sales_edit_reclaimed", "sales_edit_done", "sales_edit_failed", "dfy_build_launched",
    "prompt_rebuild_requested",
    // F170: podněty -> prompt patch, one row per step of the request.
    "prompt_patch_requested", "prompt_patch_proposed", "prompt_patch_approved",
    "prompt_patch_rejected", "prompt_patch_applied", "prompt_patch_failed",
    "price_approved", "sales_package_ready", "ai_avatar_received",
    // O9 (audit F115): closing a podnět as not relevant and deleting an own
    // note are decisions about a material, next to feedback_processed.
    "feedback_dismissed", "feedback_note_deleted",
    // Same sweep: a draft deliverable really can be deleted, and the row that
    // records it belongs beside the ones that create and publish.
    "deliverable_deleted",
    "salespage_netlify_published", "salespage_netlify_domain_configured",
    // F138 kolečko 3: the order-links lane and the partner's briefing are both
    // about a MATERIAL, and all six were falling into the generic sentence.
    "order_links_confirmed", "order_links_integrated", "order_links_integration_requested",
    "sales_edit_merged", "partner_brief_saved", "material_regeneration_requested",
    // Written when a material message could not name its own material: a
    // pipeline gap the admin has to see, which is the opposite of a row that
    // says "we recorded a change".
    "notification_label_fallback"
  ],
  profile: [
    "profile_research_requested", "profile_research_completed", "profile_research_failed",
    "profile_map_deleted", "map_apply", "map_apply_completed", "map_apply_rejected",
    "transcript_requeued"
  ],
  // S3-04: the delivery test is the second thing this tool does, so "Zprávy (0)"
  // used to stand over a day in which four tests had gone out from that panel.
  messaging: ["notification_requeued", "notification_delivery_tested"],
  imports: [
    "stripe_import_settings_updated", "answers_imported", "submission_exported",
    // F143-W3: the ONE type the "Jen prodej" import writes, whatever it touched
    // (`data_json.kind` names the piece). It belongs to Imports, not to Clients
    // beside `staff_override_approved`: nobody decided anything for the client
    // here - the client decided outside the system and the import recorded it.
    "sales_only_import"
  ],
  access: [
    "auth_login", "staff_client_viewed", "impersonation_started", "impersonation_ended",
    // F138e: the mode switch and every write a partner made for a client.
    "impersonation_acting", "impersonation_looking", "impersonated_write",
    "invite_created", "invite_accepted", "invite_revoked"
  ]
};

/** Every tool id the timeline can filter on. `system` is the complement. */
export const ACTIVITY_TOOL_IDS = [...Object.keys(ACTIVITY_TOOL_TYPES), "system"];

/**
 * Two types nobody classifies into a tool (they belong to "Systém" by right)
 * but which still reach a screen and still need a human sentence. Unlisted,
 * they read as an unknown event in the one feed opened to find out what
 * happened.
 */
export const SYSTEM_AUDIT_TYPES = [
  "r2_orphan_sweep", "dev_mail_allowlist_sent",
  // F138 kolečko 3: two master switches in Servis. They carry no client_id, so
  // they belong to no client's tool - but they DO reach the timeline, and an
  // automation somebody armed or disarmed is the first thing to check when a
  // lane goes quiet.
  "bot_transfer_settings_updated", "owner_role_settings_updated"
] as const;

/**
 * The whole inventory, sorted. This is what the client history's sentence
 * catalog is measured against.
 */
export const AUDIT_TYPES: readonly string[] = [
  ...new Set([...Object.values(ACTIVITY_TOOL_TYPES).flat(), ...SYSTEM_AUDIT_TYPES])
].sort();
